import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { saveAs } from 'file-saver'
import { nanoid } from 'nanoid'
import { kAgentV2RouterApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getFileNameFromContentDisposition } from '@/util/api/content-disposition'
import { formatUnknownError, getResponseDebugContext, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'

const kDefaultConversationFileName = 'Conversation.docx'
const kSentryEndpoint = 'GET /api/v2/agent/conversations/{conversation_id}/export-docx'

export async function downloadAgentV2ConversationDocx(conversationId: string): Promise<string> {
  const sentryTransactionId = nanoid()
  const sentryTags = {
    endpoint: kSentryEndpoint,
    transaction_id: sentryTransactionId,
  }
  const url = `${kPaxtonAppApiRESTBaseUrl()}${kAgentV2RouterApiBasePath}/conversations/${encodeURIComponent(conversationId)}/export-docx`

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error('User not authenticated')

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Transaction-Id': sentryTransactionId,
      },
    })

    if (!response.ok) {
      const { data, parseError } = await readResponseJson(response)
      const errorData = parseError ? undefined : data
      const detail = formatErrorDetail(errorData)
      Sentry.withScope((scope) => {
        scope.setTags(sentryTags)
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
          extra: {
            ...getResponseDebugContext(response, url),
            conversationId,
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })
      throw new ApiError(response.status, detail || `Conversation export failed: ${response.status}`)
    }

    const filename = getFileNameFromContentDisposition(response.headers.get('Content-Disposition')) ?? kDefaultConversationFileName
    saveAs(await response.blob(), filename)
    return filename
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags(sentryTags)
        Sentry.captureException(error, {
          extra: {
            conversationId,
            requestUrl: url,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
    }
    throw error
  }
}

function formatErrorDetail(data: unknown): string {
  const responseData = data as { detail?: unknown } | undefined
  const detail = responseData?.detail ?? data
  if (typeof detail === 'string') return detail
  if (detail == null) return ''
  return JSON.stringify(detail)
}
